import { dayKey } from "./time";
import { trainingUnitDisplayTitle } from "./trainingPlanUnits";
import { isTrainingFeedbackPending } from "./trainingFeedback";

export const isMealItem = (item) => ["meal", "nutrition"].includes(item.category);
export const isSupplementItem = (item) => ["supplement", "supplements"].includes(item.category);
const timestamp = (value) => value ? Date.parse(value) : NaN;
const doneStates = new Set(["completed", "completed_auto", "assumed_completed", "missed", "cancelled", "execution_pending"]);

export function isFutureMeal(item, now = Date.now()) {
  return isMealItem(item) && timestamp(item.start_at) > Number(now);
}

// Presentation only: permissions and mutations remain authoritative on the server.
export function canAnswerDecision(item, now = Date.now()) {
  return item.status === "pending_decision" && item.action_required && !item.decided_at
    && (item.allowed_actions || []).includes("confirm")
    && (item.allowed_actions || []).includes("decline")
    && (!item.decision_deadline || timestamp(item.decision_deadline) >= Number(now));
}

export function playerItemStatusKey(item, now = Date.now()) {
  if (item.status === "pending_decision" && !item.decided_at
    && timestamp(item.decision_deadline) < Number(now)) return "overdue";
  if (item.action_result === "confirm") return "decisionConfirmed";
  if (item.action_result === "decline") return "decisionDeclined";
  if (["match", "game"].includes(item.category)) {
    if (item.match_feedback) return "matchFeedbackSubmitted";
    if (item.status === "completed") return (item.allowed_actions || []).includes("submit_match_feedback") ? "endedGiveFeedback" : "ended";
  }
  if (isMealItem(item)) {
    if (item.status === "changed") return isFutureMeal(item, now) ? "mealExchangeScheduled" : "mealReplacementReported";
    if (["assumed_completed", "completed_auto"].includes(item.status)) return "eatenAsPlanned";
    if (item.status === "missed" && (item.allowed_actions || []).includes("correct_intake")) return "notEaten";
  }
  if (isSupplementItem(item)) {
    if (["completed", "assumed_completed", "completed_auto"].includes(item.status)) return "supplementTaken";
    if (item.status === "missed") return "forgotten";
  }
  return item.status === "execution_pending" ? "serviceExecutionPending" : item.status;
}

export function playerItemTitle(item, t) {
  if (["match", "game"].includes(item.category)) return `${t(`matchType_${item.match?.match_type || "league"}`)}${item.match?.opponent ? ` · ${item.match.opponent}` : ""}`;
  return item.training_unit_type ? trainingUnitDisplayTitle(item, t) : item.actual_title || item.title;
}

export function playerItemTone(item, now = Date.now()) {
  if (canAnswerDecision(item, now) || playerItemStatusKey(item, now) === "overdue") return "attention";
  if (item.status === "missed") return "missed";
  if (item.status === "cancelled") return "cancelled";
  if (doneStates.has(item.status) && item.status !== "execution_pending") return "done";
  return ["changed", "postponed", "execution_pending"].includes(item.status) ? "updated" : "routine";
}

// Visual accent only; does not change status, ordering or action permissions.
export function playerItemVisualVariant(item, now = Date.now(), { next = false } = {}) {
  const tone = playerItemTone(item, now);
  if (["missed", "cancelled"].includes(tone)) return "negative";
  if (tone === "attention" || isTrainingFeedbackPending(item, now)
    || playerItemStatusKey(item, now) === "endedGiveFeedback"
    || item.status === "execution_pending") return "attention";
  // Domain status wins over next; only an otherwise neutral card gets cyan.
  if (tone === "done") return "positive";
  return next ? "next" : "neutral";
}

export function mealNutrition(item) {
  const replacement = Boolean(item.actual_title || item.meal_deviation);
  const meal = item.actual_meal || (!replacement ? item.planned_meal || item.meal : null);
  return meal?.nutrition || item.meal_deviation?.assessment?.estimated_nutrition
    || item.meal_deviation?.assessment?.nutritional_assessment || null;
}

export function nutritionNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? Math.round(value) : null;
}

export function prioritizePlayerDay(items, dueDecisions, { now, date, timezone }) {
  const currentDay = dayKey(new Date(Number(now)).toISOString(), timezone) === date;
  const byId = new Map(items.map((item) => [item.id, item]));
  if (currentDay) dueDecisions.forEach((item) => { if (!byId.has(item.id)) byId.set(item.id, item); });
  const decisions = currentDay ? [...byId.values()].filter((item) => canAnswerDecision(item, now))
    .sort((a, b) => (timestamp(a.decision_deadline) || Infinity) - (timestamp(b.decision_deadline) || Infinity)
      || String(a.id).localeCompare(String(b.id))) : [];
  const decisionIds = new Set(decisions.map((item) => item.id));
  const timeline = items.filter((item) => !decisionIds.has(item.id));
  const candidates = currentDay ? timeline.filter((item) => {
    if (item.decided_at || item.category === "decision" || ["cancelled", "missed", "execution_pending", "overdue"].includes(item.status)) return false;
    if (doneStates.has(item.status)) return false;
    // "Up next" belongs after the now marker. Pending feedback keeps its
    // existing action on the past card; it is not a new future appointment.
    const start = timestamp(item.start_at);
    return Number.isFinite(start) && start > Number(now);
  }) : [];
  candidates.sort((a, b) => timestamp(a.start_at) - timestamp(b.start_at)
    || String(a.id).localeCompare(String(b.id)));
  return { decisions, timeline, nextId: candidates[0]?.id || null };
}

// Show existing preparation information, not an inferred new appointment/time.
export function nextItemPreview(item) {
  const description = (item.player_visible_description || "").trim();
  if (!description || description === item.title || description === item.actual_title) return "";
  const sentences = description.split(/\n+|(?<=[.!?])\s+/).map((line) => line.trim()).filter(Boolean);
  const preparation = sentences.find((line) => /Treffpunkt|mitbringen|vorbereit|Abfahrt|meeting point|bring|prepar|departure/i.test(line));
  const snippet = preparation || sentences[0];
  return snippet.length <= 150 ? snippet : `${snippet.slice(0, 147).trimEnd()}…`;
}
